import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import "./editor.scss";
import Switch from "react-switch";
import useKeyPress from "./useKeypress";
import Select, { components } from "react-select";
import Editor from "@monaco-editor/react";
import CodeMirror from "@uiw/react-codemirror";
import { basicSetup } from "codemirror";
import { EditorState, RangeSetBuilder } from "@codemirror/state";
import { EditorView, Decoration } from "@codemirror/view";
import { python } from "@codemirror/lang-python";
import { javascript } from "@codemirror/lang-javascript";
import { sql } from "@codemirror/lang-sql";
import { ThreeDots } from "../../Loader/loader";
import { useDispatch } from "react-redux";
import {
  compileCode,
  submitCode,
  getNotebookUrl,
  submitNotebook,
  updateTestStatus,
  getStarterCode,
  sendProctoringEvent,
} from "../../Redux/ActionCreators/candidateTestAction";
import { returnErrors } from "../../Redux/ActionCreators/errorAction";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <img src="/images/select-arrow.svg" alt="" width="15px" />
    </components.DropdownIndicator>
  );
};

// Memoized component to prevent re-renders when parent timer updates
const QuestionDescription = React.memo(({ question, type, style }) => {
  return (
    <div
      className={type === "jupyter" ? "" : "col-md-5"}
      id={type === "jupyter" ? "leftPane" : ""}
      ref={(el) => {
        if (el) {
          const key = type === "jupyter" ? "leftPaneScroll" : "mainColMd5Scroll";
          const savedScrollTop = sessionStorage.getItem(`${key}Top`);
          const savedScrollLeft = sessionStorage.getItem(`${key}Left`);
          if (savedScrollTop) el.scrollTop = parseInt(savedScrollTop);
          if (savedScrollLeft) el.scrollLeft = parseInt(savedScrollLeft);
        }
      }}
      onScroll={(e) => {
        const key = type === "jupyter" ? "leftPaneScroll" : "mainColMd5Scroll";
        sessionStorage.setItem(`${key}Top`, e.target.scrollTop);
        sessionStorage.setItem(`${key}Left`, e.target.scrollLeft);
      }}
      style={{
        ...(type === "jupyter"
          ? {
            width: "20%", // Default, overridden by style prop
            minWidth: "20%",
            flexShrink: 0,
            padding: 15,
            borderRight: "2px solid #D7DADB",
            overflowY: "auto",
            overflowX: "auto",
          }
          : {
            padding: 15,
            borderRight: "2px solid #D7DADB",
            maxHeight: "73vh",
            overflowY: "auto",
            overflowX: "auto",
          }),
        ...style // Merge passed styles (like dynamic width)
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <h5 style={{ marginBottom: 0, fontSize: 18 }}>
            {question?.currentQuestion}/{question?.totalQuestion}
          </h5>
          <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
            {question?.questionTypeID === 13
              ? "Data Science"
              : question?.questionTypeID === 12
                ? "Database"
                : "Coding"}
          </p>
        </div>
        {question?.testName && (
          <div>
            <strong style={{ fontSize: 18, color: "#333" }}>
              {question?.testName}
            </strong>
          </div>
        )}
      </div>
      <h2>{question?.question}</h2>
      <p
        className="mt-3"
        dangerouslySetInnerHTML={{
          __html: question?.description,
        }}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to ensure we only re-render if question really changes
  return (
    prevProps.question?.candidateQuestionId === nextProps.question?.candidateQuestionId &&
    prevProps.question?.description === nextProps.question?.description &&
    prevProps.type === nextProps.type
  );
});

const Main = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const notebookConnected = useRef(false);
  const urlRefreshedRef = useRef(false);
  const latestValueRef = useRef("");

  const codingNav = props.codingNav;

  const [code, setCode] = useState("");
  const [value, setValue] = useState(code || "");
  const [processing, setProcessing] = useState(null);
  const [compile, setCompile] = useState(false);
  const [check, setCheck] = useState(false);
  const [theme, setTheme] = useState("vs-light");
  const [active, setActive] = useState("");
  const [submit, setSubmit] = useState(false);
  const [outputStatus, setOutputStatus] = useState(null);
  const [output, setOutput] = useState(null);
  const [parsedOutput, setParsedOutput] = useState([]);
  const [compileTestCases, setCompileTestCases] = useState([]);
  const [notebookLoading, setNotebookLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [canLoadIframe, setCanLoadIframe] = useState(false);
  const [notebookUrlLocal, setNotebookUrlLocal] = useState("");
  const [leftWidth, setLeftWidth] = useState("50%");
  const leftPaneStyle = useMemo(() => ({ width: leftWidth }), [leftWidth]);
  const [connectTimer, setConnectTimer] = useState(180);
  const [showRetry, setShowRetry] = useState(false);
  const [runInput, setRunInput] = useState("");
  const [compail, setCompail] = useState(false);
  const [showNotebookModal, setShowNotebookModal] = useState(false);
  const [showSubmitTestPopup, setShowSubmitTestPopup] = useState(false);
  const [isNotebookSubmitTriggered, setIsNotebookSubmitTriggered] = useState(false);
  // Derived from Redux so it survives Main unmount/remount (e.g. during questionLoading)
  // True once history is fully populated (user has been to the last question)
  const hasReachedEnd = codingNav?.history?.length > 0 &&
    props.question?.totalQuestion > 0 &&
    codingNav.history.length >= props.question.totalQuestion;
  const [showNextConfirmPopup, setShowNextConfirmPopup] = useState(false);
  const pendingNextPayloadRef = useRef(null);
  const [showNotebookNote, setShowNotebookNote] = useState(() => {
    return sessionStorage.getItem("hideNotebookNote") !== "true";
  });
  const [isFadingOutNote, setIsFadingOutNote] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [navLoading, setNavLoading] = useState(false);
  const hasUserEditedRef = useRef(false);
  const isResettingRef = useRef(false);
  const notebookSubmitInFlightRef = useRef(false);

  const retryLimit = 12;

  const [language, setLanguage] = useState(null);

  const globalStarterCode = useSelector(state => state.candidateTest?.starterCode);
  const starterCode = props.question?.starterCode || globalStarterCode;
  const isStubEditor = props.question?.questionTypeID === 7 && starterCode?.useStub === true;

  const stub = useMemo(() => {
    if (!starterCode?.stubs?.length) return null;
    const langName =
      language?.value?.toLowerCase() ||
      props.question?.languages?.[0]?.codingLanguage?.toLowerCase() ||
      props.question?.databaseLanguages?.[0]?.databaseLanguage?.toLowerCase();
    const langId =
      language?.id ||
      props.question?.languages?.[0]?.codingLanguageId ||
      props.question?.databaseLanguages?.[0]?.codingLanguageId;
    return (
      starterCode.stubs.find((s) =>
        typeof s.language === "number"
          ? s.language === langId
          : String(s.language).toLowerCase() === langName
      ) ?? starterCode.stubs[0]
    );
  }, [starterCode, language, props.question?.languages?.[0]?.codingLanguage, props.question?.databaseLanguages?.[0]?.databaseLanguage]);

  const normalize = (s = "") => s.replace(/\r\n/g, "\n");

  // Modified: Prioritize persisted head/tail/body from editorState if available
  const savedState = props.question?.editorState;

  const head = useMemo(() => {
    if (savedState?.head !== undefined) return savedState.head;
    return normalize(stub?.head || "");
  }, [stub?.head, savedState?.head]);

  const tail = useMemo(() => {
    if (savedState?.tail !== undefined) return savedState.tail;
    const t = normalize(stub?.tail || "");
    return t.trim().length === 0 ? "" : t;
  }, [stub?.tail, savedState?.tail]);

  const stubBody = useMemo(() => {
    // If we have a saved stubBody (the user's code only), use that.
    // Otherwise fallback to the default stub body.
    if (savedState?.stubBody !== undefined) return normalize(savedState.stubBody);
    return normalize(stub?.body || "");
  }, [stub?.body, savedState?.stubBody]);

  const [editableBody, setEditableBody] = useState(stubBody);
  const [stubCode, setStubCode] = useState("");

  const visibleTestCases = compileTestCases.filter(tc => tc.hidden === false);
  const firstVisible = visibleTestCases[0];

  const passCount = visibleTestCases.filter(tc => tc.executionStatus === 1).length;
  const totalCount = visibleTestCases.length;

  const allPassed = totalCount > 0 && passCount === totalCount;

  const codeMeta = useMemo(() => {
    if (!isStubEditor) return null;
    return {
      fullCode: `${head}${stubBody}${tail}`,
      headLength: head.length,
      tailLength: tail.length,
      bodyStart: head.length,
    };
  }, [isStubEditor, head, stubBody, tail]);

  const lockOutsideBody = (meta) =>
    EditorState.transactionFilter.of((tr) => {
      if (!tr.docChanged) return tr;

      const docLength = tr.startState.doc.length;
      const tailStart = docLength - meta.tailLength;

      let invalid = false;

      tr.changes.iterChanges((from, to) => {
        if (from < meta.bodyStart || to > tailStart) {
          invalid = true;
        }
      });

      return invalid ? [] : tr;
    });

  const lockedTheme = EditorView.baseTheme({
    ".cm-locked-line": {
      backgroundColor: "#FFC1074D",
    },
    ".cm-locked-line *": {
      cursor: "not-allowed",
    },
  });

  const lockedDecorations = (meta) =>
    EditorView.decorations.compute([], (state) => {
      const builder = new RangeSetBuilder();
      const doc = state.doc;
      const tailStart = doc.length - meta.tailLength;

      // Guard: document hasn't caught up to the new meta yet — skip decorations.
      if (tailStart < 0 || tailStart < meta.bodyStart) {
        return builder.finish();
      }

      let line = doc.lineAt(0);
      while (line.from < meta.bodyStart) {
        builder.add(
          line.from,
          line.from,
          Decoration.line({ class: "cm-locked-line" })
        );
        if (line.number === doc.lines) break;
        line = doc.line(line.number + 1);
      }

      // ----- TAIL -----
      if (meta.tailLength > 0) {
        line = doc.lineAt(tailStart);
        while (line.from <= doc.length) {
          builder.add(
            line.from,
            line.from,
            Decoration.line({ class: "cm-locked-line" })
          );
          if (line.number === doc.lines) break;
          line = doc.line(line.number + 1);
        }
      }

      return builder.finish();
    });

  const languageOptions =
    props.question?.questionTypeID === 12
      ? (props.question?.databaseLanguages?.length > 0
        ? props.question?.databaseLanguages?.map((el) => ({
          label: el.displayLanguage,
          value: el.databaseLanguage,
          id: el.codingLanguageId,
        }))
        : props.question?.languages?.map((el) => ({
          label: el.displayLanguage,
          value: el.codingLanguage,
          id: el.codingLanguageId,
        }))
      )
      : props.question?.languages?.map((el) => ({
        label: el.displayLanguage,
        value: el.codingLanguage,
        id: el.codingLanguageId,
      }));

  const cmLanguage = useMemo(() => {
    if (language?.value === "python") return python();
    if (language?.value === "sql") return sql();
    return javascript();
  }, [language]);

  const isSubmitted = props.question?.submitted === true;

  const notebookUrls = useSelector((state) => state.candidateTest?.notebookUrls || {});
  const submittedNotebookIds = useSelector((state) => state.candidateTest?.submittedNotebookIds || {});
  const isNotebookSubmitted =
    !!submittedNotebookIds[props?.question?.candidateQuestionId] ||
    props.question?.isSubmitted === true ||
    (props.question?.questionTypeID === 13 && props.question?.submitted === true);

  const isLastIndex = (codingNav.currentIndex === codingNav.history.length - 1 && codingNav.history.length === props.question.totalQuestion) || (props.question?.currentQuestion === props.question?.totalQuestion);

  const headLines = useMemo(() => (head ? head.split("\n").length : 0), [head]);

  const tailLines = useMemo(() => (tail ? tail.split("\n").length : 0), [tail]);

  const extractBodyFromEditor = () => {
    const full = latestValueRef.current || "";
    if (!isStubEditor) return full;

    const headLength = head.length;
    const tailLength = tail.length;

    const bodyStart = headLength;
    const bodyEnd = full.length - tailLength;

    if (bodyEnd < bodyStart) return "";

    return full.slice(bodyStart, bodyEnd);
  };

  const liveBody = extractBodyFromEditor();

  const sourceCode = isStubEditor ? `${head}${liveBody}${tail}` : code;

  const enterPress = useKeyPress("Enter");
  const ctrlPress = useKeyPress("Control");

  useEffect(() => {
    if (!isStubEditor) return;
    setEditableBody(stubBody);
  }, [isStubEditor, stubBody]);

  useEffect(() => {
    hasUserEditedRef.current = false;
    setNavLoading(false);
    setCompile(false);
    setCompail(false);
    setActive("");
    setOutputStatus(null);
    setCompileTestCases([]);
    setOutput(null);
    setParsedOutput([]);
    setRunInput("");
    setIsNotebookSubmitTriggered(false);
    setIsCompiling(false);
    notebookSubmitInFlightRef.current = false;
  }, [props.question?.candidateQuestionId]);

  useEffect(() => {
    if (!props.question) return;

    const saved = props.question?.editorState;

    if (isStubEditor) {
      const savedHead = saved?.stubHead ?? head;
      const savedBody = saved?.stubBody ?? stubBody;
      const savedTail = saved?.stubTail ?? tail;

      const full = `${savedHead}${savedBody}${savedTail}`;

      setEditableBody(savedBody);
      setStubCode(full);
      setCode(full);
      setValue(full);
      latestValueRef.current = full;
    } else {
      const reduxCode = saved?.code ?? "";
      setCode(reduxCode);
      setValue(reduxCode);
      latestValueRef.current = reduxCode;
    }
  }, [props.question?.candidateQuestionId]);

  useEffect(() => {
    if (!props.question) return;

    const savedLang = props.question.editorState?.language;
    if (savedLang?.value) {
      setLanguage(savedLang);
      return;
    }

    if (languageOptions && languageOptions.length > 0) {
      setLanguage(languageOptions[0]);
    } else {
      setLanguage(null);
    }
  }, [props.question?.candidateQuestionId]);

  useEffect(() => {
    if (compile) {
      setTimeout(() => {
        const elem = document.querySelector(".complie-window");
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
    }
  }, [compile, active]);

  const handleChangeMode = async (e) => {
    await setCheck(e);
    await setTheme(e ? "vs-dark" : "vs-light");
  };

  const onSelectChange = (sl) => {
    setLanguage(sl);

    if (isStubEditor && starterCode?.stubs?.length) {
      const newStub =
        starterCode.stubs.find((s) =>
          typeof s.language === "number"
            ? s.language === sl?.id
            : String(s.language).toLowerCase() === sl?.value?.toLowerCase()
        ) ?? starterCode.stubs[0];

      if (newStub) {
        const newHead = normalize(newStub.head || "");
        const newBody = normalize(newStub.body || "");
        const rawTail = normalize(newStub.tail || "");
        const newTail = rawTail.trim().length === 0 ? "" : rawTail;
        const full = `${newHead}${newBody}${newTail}`;

        setEditableBody(newBody);
        setStubCode(full);
        setCode(full);
        setValue(full);
        latestValueRef.current = full;

        props.onCodeChange?.({
          stubHead: newHead,
          stubBody: newBody,
          stubTail: newTail,
          code: full,
          language: sl,
        });
      }
    } else if (!isStubEditor) {
      props.onCodeChange?.({ code, language: sl });
    }
  };

  const handleEditorChange = (val) => {
    if (!isResettingRef.current) {
      hasUserEditedRef.current = true;
    }
    setValue(val);
    setCode(val);
    latestValueRef.current = val;

    if (isStubEditor) {
      const normalized = val.replace(/\r\n/g, "\n");
      const lines = normalized.split("\n");

      // const body = lines
      //   .slice(headLines, lines.length - tailLines)
      //   .join("\n");
      const bodyStart = head.length;
      const tailStart = normalized.length - tail.length;
      const body = normalized.substring(bodyStart, tailStart);

      setEditableBody(body);

      props.onCodeChange?.({
        stubHead: head,
        stubBody: body,
        stubTail: tail,
        code: `${head}${body}${tail}`,
        language,
      });
    } else {
      props.onCodeChange?.({
        code: val,
        language,
      });
    }
  };

  useEffect(() => {
    if (enterPress && ctrlPress) {
      handleCompile();
    }
  }, [ctrlPress, enterPress]);

  const onChangeCode = (action, data) => {
    switch (action) {
      case "code": {
        setCode(data);
        setValue(data);
        break;
      }
      default: {
        console.warn(t("case not handled!"), action, data);
      }
    }
  };

  const handleCompile = async (e) => {
    e.preventDefault();
    if (props.question?.questionTypeID === 13) return;

    setIsCompiling(true);
    setCompail(true);
    setCompile(true);
    setActive("run");

    setOutputStatus(null);
    setCompileTestCases([]);
    setRunInput("");

    const liveBody = extractBodyFromEditor();

    const sourceCode = isStubEditor ? `${head}${liveBody}${tail}` : code;

    const formData = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question.candidateQuestionId,
      language: language?.value,
      sourceCode: sourceCode,
      inputs: props.question.inputs || [],
      output: "",
    };

    if (!language?.value) {
      setIsCompiling(false);
      setCompail(false);
      returnErrors(dispatch, t("Please select language"), 500);
    } else {
      const res = await dispatch(compileCode(formData, props.token));
      if (res?.type === "RUN_CODE") {
        setIsCompiling(false);
        setCompail(false);

        const oc = res?.payload?.ocResponse;

        setOutputStatus(oc);
        setCompileTestCases(res?.payload?.testCases || []);

        if (oc?.useInput && oc?.input) {
          setRunInput(oc.input);
        } else {
          setRunInput("");
        }

        setOutput(oc?.stdout);
        setParsedOutput(oc?.parsedOutput || []);
        onChangeCode("code", code);
      }
    }
    setIsCompiling(false);
    setCompail(false);
  };

  const handleSubmitNotebook = async () => {
    if (isNotebookSubmitted || isNotebookSubmitTriggered || notebookSubmitInFlightRef.current) return;
    notebookSubmitInFlightRef.current = true;

    setIsNotebookSubmitTriggered(true);
    setProcessing(true);
    setCompail(true);

    const body = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question?.candidateQuestionId,
    };

    await dispatch(submitNotebook(body, props.token));
    notebookConnected.current = false;
    setCanLoadIframe(false);
    setNotebookUrlLocal("");
    setConnectTimer(180);
    setProcessing(false);
    setCompail(false);

    if (isLastIndex) {
      return;
    }

    await goNext();
  };

  const codeSubmit = async () => {
    if (isSubmitted || isNotebookSubmitted) return;

    await setProcessing(true);

    if (props.question?.questionTypeID === 13) {
      return handleSubmitNotebook();
    }

    if (!language?.value) {
      returnErrors(dispatch, t("Please select language"), 500);
      setProcessing(false);
    } else {
      let obj = {
        token: props.inviteToken,
        candidateTestId: props.question?.candidateTestId,
        candidateTestQuestionId: props.question.candidateQuestionId,
        sourceCode: sourceCode,
        language: language?.value,
        inputs: props.question.inputs || [],
        output: "",
      };
      const res = await dispatch(submitCode(obj, props.token));
      if (res?.type === "SUBMIT_CODE") {
        dispatch({ type: "CODING_NAV_MARK_SUBMITTED" });

        if (isLastIndex) {
          setProcessing(false);
          return;
        }

        props.onNext(persistEditorState());
      }
    }
    await setProcessing(false);
  };

  const goNext = () => {
    props.getNext();
  };

  useEffect(() => {
    if (props?.question?.questionTypeID !== 13) return;

    // Skip connection if already submitted: Redux-tracked, API isSubmitted, or nav-history submitted flag
    const alreadySubmitted =
      !!submittedNotebookIds[props?.question?.candidateQuestionId] ||
      props.question?.isSubmitted === true ||
      props.question?.submitted === true;
    if (alreadySubmitted) {
      setCanLoadIframe(false);
      setNotebookUrlLocal("");
      setNotebookLoading(false);
      setLoadingFailed(false);
      setRetryCount(0);
      notebookConnected.current = false;
      return;
    }

    notebookConnected.current = false;
    urlRefreshedRef.current = false;

    setCanLoadIframe(false);
    setNotebookUrlLocal("");
    setLoadingFailed(false);
    setRetryCount(0);

    setConnectTimer(180);
    setNotebookLoading(true);

    props.pauseTestTimer?.();

    // Use JupyterUrl from questionMetaData (available on resume) before falling back to the API
    // questionMetaData is a JSON string: { JupyterUrl, ContainerId, ... }
    let metaUrl = null;
    try {
      if (props.question?.questionMetaData) {
        const parsed = typeof props.question.questionMetaData === "string"
          ? JSON.parse(props.question.questionMetaData)
          : props.question.questionMetaData;
        metaUrl = parsed?.JupyterUrl ?? null;
      }
    } catch (e) {
      metaUrl = null;
    }
    if (metaUrl) {
      setNotebookUrlLocal(metaUrl);
      attemptNotebookLoad(metaUrl);
    } else {
      loadNotebookUrl();
    }
  }, [props?.question?.candidateQuestionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!notebookLoading) return;

    let interval = setInterval(() => {
      setConnectTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [notebookLoading]);



  const loadNotebookUrl = () => {
    const qId = props.question.candidateQuestionId;
    // Check cache first
    if (notebookUrls[qId]) {
      const cachedUrl = notebookUrls[qId];
      setNotebookUrlLocal(cachedUrl);
      attemptNotebookLoad(cachedUrl);
      return;
    }

    dispatch(
      getNotebookUrl(
        props.inviteToken,
        props.question.candidateTestId,
        qId,
        props.token
      )
    ).then((res) => {
      // payload is now { url, candidateTestQuestionId } due to my change
      // OR handle fail
      const freshUrl = res?.payload?.url;

      if (!freshUrl) {
        setLoadingFailed(true);
        setNotebookLoading(false);
        setConnectTimer(180);
        if (props.startTimer && props.question?.questionTypeID === 13) {
          props.startTimer();
        }
        return;
      }
      setNotebookUrlLocal(freshUrl);
      attemptNotebookLoad(freshUrl);
    });
  };

  const attemptNotebookLoad = async (url, attempt = 0) => {
    if (!url) return;

    setNotebookLoading(true);

    const { ok, status, error } = await verifyNotebookUrl(url);

    if (ok) {
      notebookConnected.current = true;

      // Delay iframe mount by 10 seconds
      setTimeout(() => {
        setCanLoadIframe(true);
        setNotebookLoading(false);
      }, 45000);

      return;
    }

    dispatch(
      sendProctoringEvent(
        {
          candidateTestId: props.question?.candidateTestId,
          eventType: "LOG",
          duration: 0,
          details: JSON.stringify({
            message: "Notebook server connection failed",
            event: "notebook_connection_failed",
            attempt: attempt + 1,
            totalAttempts: retryLimit,
            httpStatus: status ?? null,
            error: error ?? null,
          }),
        },
        props.token
      )
    );

    if (attempt < retryLimit - 1) {
      setRetryCount(attempt + 1);

      setTimeout(() => {
        attemptNotebookLoad(url, attempt + 1);
      }, 15000);
    } else if (!urlRefreshedRef.current) {
      // All retries exhausted — fetch a fresh URL once and restart
      urlRefreshedRef.current = true;
      const qId = props.question?.candidateQuestionId;
      dispatch(
        getNotebookUrl(props.inviteToken, props.question?.candidateTestId, qId, props.token)
      ).then((res) => {
        const freshUrl = res?.payload?.url;
        if (freshUrl) {
          setRetryCount(0);
          setNotebookUrlLocal(freshUrl);
          attemptNotebookLoad(freshUrl, 0);
        } else {
          setLoadingFailed(true);
          setNotebookLoading(false);
          setConnectTimer(180);
          props.resumeTestTimer?.();
          if (!showRetry) setShowRetry(true);
        }
      });
    } else {
      setLoadingFailed(true);
      setNotebookLoading(false);
      setConnectTimer(180);
      props.resumeTestTimer?.();
      if (!showRetry) {
        setShowRetry(true);
      }
    }
  };

  const handleIframeLoad = () => {
    if (!notebookConnected.current) {
      notebookConnected.current = true;
    }

    // After this moment: NEVER update any notebook state again.
    setNotebookLoading(false);
    setLoadingFailed(false);
    setConnectTimer(180);

    props.resumeTestTimer?.();

    // if (props.uploadImage && props.question?.questionTypeID === 13) {
    //   props.uploadImage();
    // }
  };

  const verifyNotebookUrl = async (url) => {
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        return { ok: false, status: res.status, error: `HTTP ${res.status} ${res.statusText}`.trim() };
      }
      return { ok: true, status: res.status };
    } catch (err) {
      return { ok: false, status: null, error: err.message };
    }
  };

  const dragOverlayRef = useRef(null);

  const startDrag = (e) => {
    e.preventDefault();

    const container = document.getElementById("resizeContainer");
    const leftPaneEl = document.getElementById("leftPane");
    if (!container || !leftPaneEl) return;

    // Show overlay synchronously — blocks the iframe from capturing events.
    // We must NOT call setLeftWidth (setState) during the drag because every
    // React re-render would reset overlay.style.display back to "none" (React
    // always reconciles inline styles from the virtual DOM).  Instead we
    // manipulate the left-pane width directly in the DOM and only sync React
    // state once on mouse-up.
    if (dragOverlayRef.current) {
      dragOverlayRef.current.style.display = "block";
    }

    const rect = container.getBoundingClientRect();
    let finalWidth = leftPaneEl.style.width || "50%";

    const onMouseMove = (event) => {
      const x = event.clientX - rect.left;
      const newWidth = (x / rect.width) * 100;
      if (newWidth >= 20 && newWidth <= 80) {
        finalWidth = newWidth + "%";
        leftPaneEl.style.width = finalWidth; // direct DOM – no React re-render
      }
    };

    const stopDrag = () => {
      if (dragOverlayRef.current) {
        dragOverlayRef.current.style.display = "none";
      }
      setLeftWidth(finalWidth); // sync React state once at the end
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
  };

  const handleClearCode = async () => {
    const qid = props.question?.candidateQuestionId;
    const langValue = language?.value;

    isResettingRef.current = true;
    hasUserEditedRef.current = false;

    if (!langValue || !qid) {
      setCode("");
      setValue("");
      setStubCode("");
      setEditableBody("");
      latestValueRef.current = "";
      setTimeout(() => { isResettingRef.current = false; }, 100);
      return;
    }

    setIsClearing(true);

    try {
      const res = await dispatch(
        getStarterCode(
          props.inviteToken,
          props.question?.candidateTestId,
          qid,
          langValue,
          props.token
        )
      );

      if (res?.type === "GET_STARTER_CODE") {
        dispatch({
          type: "CODING_NAV_UPDATE_STARTER_CODE",
          payload: { ...res.payload, code: res.payload.useStub ? "" : res.payload.code },
        });

        if (res.payload.useStub) {
          const newHead = normalize(res.payload.stubs?.[0]?.head || "");
          const newBody = normalize(res.payload.stubs?.[0]?.body || "");
          const newTail = normalize(res.payload.stubs?.[0]?.tail || "");
          const full = `${newHead}${newBody}${newTail}`;
          setEditableBody(newBody);
          setStubCode(full);
          setCode(full);
          setValue(full);
          latestValueRef.current = full;
        } else {
          const freshCode = res.payload.code || "";
          setCode(freshCode);
          setValue(freshCode);
          latestValueRef.current = freshCode;
        }
      } else {
        setCode("");
        setValue("");
        setStubCode("");
        setEditableBody("");
        latestValueRef.current = "";
      }
    } catch {
      setCode("");
      setValue("");
      setStubCode("");
      setEditableBody("");
      latestValueRef.current = "";
    }

    setTimeout(() => { isResettingRef.current = false; }, 100);
    setIsClearing(false);
  };

  const persistEditorState = () => {
    if (isStubEditor) {
      const body = extractBodyFromEditor();

      return {
        editorState: {
          stubHead: head,
          stubBody: body,
          stubTail: tail,
          code: `${head}${body}${tail}`,
          language,
        },
      };
    }

    return {
      editorState: {
        code,
        language,
      },
    };
  };

  return (
    <>
      {(navLoading || processing) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="spinner-border"
            role="status"
            style={{ width: "1rem", height: "1rem", color: "#f75891" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <div
        className="coding-container"
        style={
          props.question?.questionTypeID === 13
            ? { width: "100%" }
            : { width: "90vw" }
        }
      >
        <>
          <div className="coding-nav-bar d-flex justify-content-between" style={{ margin: props.question?.questionTypeID === 13 ? '5px 5%' : '5px 0' }}>
            <button
              className="submit-btn"
              disabled={codingNav.currentIndex === 0 || notebookLoading || processing || navLoading}
              onClick={() => {
                if (props.question?.questionTypeID === 13) {
                  setNotebookLoading(true);
                }
                setNavLoading(true);
                props.onPrev(persistEditorState());
              }}
              style={{ background: codingNav.currentIndex === 0 || notebookLoading || navLoading ? "gray" : '', width: '180px' }}
            >
              Prev
            </button>

            {isSubmitted && (
              <span style={{ margin: "0 12px", color: "red" }}>
                This question has already been submitted
              </span>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button className="submit-btn"
                style={{ background: isLastIndex || notebookLoading || navLoading ? "gray" : '', width: '180px' }}
                disabled={isLastIndex || notebookLoading || processing || navLoading}
                onClick={() => {
                  const payload = persistEditorState();
                  if (!isSubmitted && !isNotebookSubmitted && (hasUserEditedRef.current || props.question?.questionTypeID === 13)) {
                    pendingNextPayloadRef.current = payload;
                    setShowNextConfirmPopup(true);
                  } else {
                    if (props.question?.questionTypeID === 13) {
                      setNotebookLoading(true);
                    }
                    setNavLoading(true);
                    props.onNext(payload);
                  }
                }}
              >
                Next
              </button>
              {hasReachedEnd && (
                <button
                  className="submit-btn"
                  style={{ background: '#f75891', width: '180px' }}
                  disabled={notebookLoading || processing || navLoading}
                  onClick={() => setShowSubmitTestPopup(true)}
                >
                  Submit Test
                </button>
              )}
            </div>
          </div>
          {props.question?.questionTypeID === 13 && showNotebookNote && (
            <div
              style={{
                margin: "6px 5% 0",
                padding: "10px 14px",
                border: "1.5px solid #E6A817",
                borderRadius: 6,
                background: "#FFFBEF",
                color: "#7A4F00",
                fontSize: 13,
                fontWeight: 500,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                transition: "opacity 0.5s ease-out",
                opacity: isFadingOutNote ? 0 : 1,
              }}
            >
              <span style={{ fontSize: 16, lineHeight: "1.3", flexShrink: 0 }}>⚠️</span>
              <span style={{ flex: 1 }}>
                <strong>Note:</strong> Do not create any new files or folders, especially Interactive Python Notebook (.ipynb) files, inside the Jupyter Notebook environment. Work only within the provided notebook file. Any work done in newly created files will not be considered during evaluation.
              </span>
              <button
                onClick={() => {
                  setIsFadingOutNote(true);
                  sessionStorage.setItem("hideNotebookNote", "true");
                  setTimeout(() => {
                    setShowNotebookNote(false);
                  }, 500);
                }}
                title="Dismiss"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#7A4F00",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "0 0 0 8px",
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
          )}
        </>

        <div className="d-flex" style={{ border: "1px solid #D7DADB" }}>
          {props.question?.questionTypeID === 13 ? (
            notebookLoading ? (
              <div
                className="loading-overlay"
                style={{
                  display: "flex",
                  width: "100%",
                  height: "73vh",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  background: "#fff",
                  zIndex: 20,
                }}
              >
                <h3 style={{ color: "#000", marginBottom: 15 }}>
                  Connecting to Jupyter Notebook...
                </h3>

                <p style={{ fontSize: 18, marginBottom: 20 }}>
                  Time remaining: {connectTimer}s
                </p>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">
                    Connecting to notebook...
                  </span>
                </div>
                {retryCount > 0 && (
                  <div style={{ marginTop: 10, textAlign: "center" }}>
                    Retrying to connect... ({retryCount}/{retryLimit})
                  </div>
                )}
              </div>
            ) : (
              <div
                id="resizeContainer"
                style={{
                  display: "flex",
                  width: "100%",
                  height: "73vh",
                  position: "relative",
                }}
              >
                {/* Full-viewport overlay that blocks the iframe from capturing
                    mouse events while the splitter is being dragged */}
                <div
                  ref={dragOverlayRef}
                  style={{
                    display: "none",
                    position: "fixed",
                    inset: 0,
                    zIndex: 9999,
                    cursor: "col-resize",
                  }}
                />
                <QuestionDescription question={props.question} type="jupyter" style={leftPaneStyle} />
                <div
                  onMouseDown={startDrag}
                  style={{
                    width: "6px",
                    background: "linear-gradient(to bottom, #bbb, #888)",
                    cursor: "col-resize",
                    borderLeft: "2px solid #666",
                    borderRight: "2px solid #666",
                    height: "100%",
                    flexShrink: 0,
                  }}
                />
                <div
                  id="rightPane"
                  style={{
                    flex: "1 1 auto",
                    minWidth: "20%",
                    overflow: "hidden",
                    padding: 0,
                  }}
                >
                  <Fragment>
                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="lang-dd"></div>

                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="mt-2" style={{ height: "64vh" }}>

                      {isNotebookSubmitted ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: "#000",
                            fontSize: 20,
                            fontWeight: 600,
                            textAlign: "center",
                            gap: 12,
                          }}
                        >
                          <span>Question already been submitted</span>
                        </div>
                      ) : (
                        <>
                          {loadingFailed && (
                            <div
                              style={{
                                padding: 20,
                                color: "red",
                                textAlign: "center",
                                marginTop: "20%"
                              }}
                            >
                              Failed to load notebook after attempts.
                              <br />
                              Please check your internet and try again.
                              <br />
                              {showRetry ? (
                                <button
                                  style={{
                                    marginTop: 15,
                                    padding: "10px 20px",
                                    background: "#f75891",
                                    color: "white",
                                    borderRadius: 6,
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setLoadingFailed(false);
                                    setRetryCount(0);
                                    setShowRetry(false);
                                    setNotebookLoading(true);
                                    loadNotebookUrl();
                                  }}
                                >
                                  Retry Connecting
                                </button>
                              ) : (
                                <p>No more retries available.</p>
                              )}
                            </div>
                          )}

                          {canLoadIframe && notebookUrlLocal && !loadingFailed && (
                            <iframe
                              src={notebookUrlLocal}
                              title="Data Science Notebook"
                              width="100%"
                              height="100%"
                              style={{ border: "none" }}
                              onLoad={handleIframeLoad}
                              onError={() => {
                                dispatch(
                                  sendProctoringEvent(
                                    {
                                      candidateTestId: props.question?.candidateTestId,
                                      eventType: "LOG",
                                      duration: 0,
                                      data: JSON.stringify({
                                        event: "notebook_iframe_load_failed",
                                        url: notebookUrlLocal,
                                      }),
                                    },
                                    props.token
                                  )
                                );
                                setLoadingFailed(true);
                                setCanLoadIframe(false);
                                setNotebookLoading(false);
                                setShowRetry(true);
                                props.resumeTestTimer?.();
                              }}
                            />
                          )}
                        </>
                      )}
                    </div>
                    {!isNotebookSubmitted && (
                      <div className="run-code-view">
                        <div className="run-header">
                          <button
                            className="submit-btn"
                            disabled={processing || notebookLoading || isNotebookSubmitTriggered}
                            onClick={() => setShowNotebookModal(true)}
                            style={{
                              backgroundColor: (processing || notebookLoading || isNotebookSubmitTriggered) ? "#D3D3D3" : "#f75891",
                              cursor: (processing || notebookLoading || isNotebookSubmitTriggered) ? "not-allowed" : "pointer",
                            }}
                          >
                            {processing ? "Submitting..." : isNotebookSubmitTriggered ? "Submitted" : "SUBMIT NOTEBOOK"}
                          </button>
                        </div>
                      </div>
                    )}

                    {showNotebookModal && (
                      <div className="upload-modal">
                        <div className="modal-content" style={{ width: "500px" }}>
                          <div className="modal-head">
                            <div></div>
                            <h3>Submit Notebook</h3>
                            <img
                              src="/images/close-icon.svg"
                              alt=""
                              onClick={() => setShowNotebookModal(false)}
                            />
                          </div>
                          <div
                            className="modal-input d-flex flex-column align-items-start"
                            style={{ gap: "15px", padding: "15px" }}
                          >
                            <p style={{ margin: 0, fontSize: 15, color: "#333" }}>
                              Please make sure you have saved all your changes in the notebook before submitting. Unsaved changes will not be captured.
                            </p>
                            <p style={{ margin: 0, fontSize: 15, color: "#333" }}>
                              Are you sure you want to submit?
                            </p>
                          </div>
                          <div className="d-flex justify-content-center" style={{ gap: "12px", paddingBottom: "15px" }}>
                            <button
                              className="submit-btn"
                              onClick={() => setShowNotebookModal(false)}
                              style={{ backgroundColor: "#aaa", cursor: "pointer" }}
                            >
                              Cancel
                            </button>
                            <button
                              className="submit-btn"
                              disabled={isNotebookSubmitTriggered || processing}
                              onClick={() => {
                                setShowNotebookModal(false);
                                handleSubmitNotebook();
                              }}
                              style={{
                                backgroundColor: (isNotebookSubmitTriggered || processing) ? "#D3D3D3" : "#f75891",
                                cursor: (isNotebookSubmitTriggered || processing) ? "not-allowed" : "pointer",
                              }}
                            >
                              Submit
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Fragment>
                </div>
              </div>
            )
          ) : (
            <Fragment>
              <QuestionDescription question={props.question} type="normal" />
              <div className="col-md-7">
                {!submit ? (
                  <Fragment>
                    <div className="lang-dd">
                      <div class="d-flex  align-items-center">
                        <Switch
                          onChange={(e) => {
                            handleChangeMode(e);
                          }}
                          width={30}
                          height={16}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          onColor="#02c573"
                          checked={check}
                        />
                        &nbsp;&nbsp;
                        <label className="col-form-label">{t("Dark Mode")}</label>
                        &nbsp;&nbsp;
                      </div>
                      <div
                        class="d-flex  align-items-center"
                        style={{ width: "40%" }}
                      >
                        <label className="col-form-label">{t("Language")}</label>
                        &nbsp;&nbsp;
                        <div style={{ width: "70%" }}>
                          <Select
                            components={{ DropdownIndicator }}
                            placeholder={`Filter By Category`}
                            options={languageOptions}
                            // styles={customStyles}
                            value={language}
                            onChange={(selectedOption) =>
                              onSelectChange(selectedOption)
                            }
                          />
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button
                          className="submit-btn"
                          disabled={isClearing || processing || isSubmitted}
                          onClick={handleClearCode}
                          style={{
                            background: (isClearing || processing || isSubmitted) ? "#D3D3D3" : "#f75891",
                            cursor: (isClearing || processing || isSubmitted) ? "not-allowed" : "pointer",
                            width: 120,
                            fontSize: 12,
                          }}
                        >
                          {isClearing ? "Clearing..." : "Clear Code"}
                        </button>
                      </div>
                    </div>
                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="mt-2">
                      {isStubEditor ? (
                        <CodeMirror
                          key={`codemirror-${props.question?.candidateQuestionId}-${language?.value || ''}`}
                          value={stubCode}
                          height="60vh"
                          extensions={[
                            basicSetup,
                            cmLanguage,
                            ...(codeMeta
                              ? [
                                lockOutsideBody(codeMeta),
                                lockedDecorations(codeMeta),
                                lockedTheme,
                              ]
                              : []),
                          ]}
                          onChange={(val) => {
                            if (!isResettingRef.current) {
                              hasUserEditedRef.current = true;
                            }
                            setStubCode(val);
                            latestValueRef.current = val;
                          }}
                        />
                      ) : (
                        <Editor
                          key={`monaco-${props.question?.candidateQuestionId}`}
                          height="60vh"
                          width={`100%`}
                          language={language?.value}
                          value={value}
                          theme={theme}
                          // defaultValue="// some comment"
                          onChange={handleEditorChange}
                          options={{
                            autoIndent: "full",
                            contextmenu: true,
                            fontFamily: "monospace",
                            lineHeight: 24,
                            hideCursorInOverviewRuler: true,
                            inlineSuggest: true,
                            fontSize: "16px",
                            formatOnType: true,
                            autoClosingBrackets: true,
                            minimap: { enabled: false },
                            scrollbar: {
                              horizontalSliderSize: 4,
                              verticalSliderSize: 18,
                            },
                            selectOnLineNumbers: true,
                            roundedSelection: false,
                            readOnly: isSubmitted,
                            cursorStyle: "line",
                            automaticLayout: true,
                          }}
                        />
                      )}

                      <div className="run-code-view">
                        <div className="run-header">
                          <button
                            className="cursor-pointer submit-btn"
                            disabled={isCompiling || processing || isSubmitted}
                            onClick={(e) => handleCompile(e)}
                            style={{ background: (isCompiling || processing || isSubmitted) ? '#D3D3D3' : '', cursor: (isCompiling || processing || isSubmitted) ? "not-allowed" : "pointer" }}
                          >
                            {isCompiling ? t("Running...") : t("COMPILE AND RUN CODE")}
                          </button>
                          <button
                            className="submit-btn"
                            disabled={isCompiling || processing || isSubmitted}
                            onClick={() => codeSubmit()}
                            style={{
                              backgroundColor: isSubmitted ? "#1B8E3E" : (isCompiling || processing ? "#D3D3D3" : "#f75891"),
                              cursor: (isCompiling || processing || isSubmitted) ? "not-allowed" : "pointer",
                            }}
                          >
                            {isSubmitted ? "✓ Submitted" : t("SUBMIT CODE")}
                          </button>
                        </div>
                        {compile ? (
                          <div className="complie-window" style={{ minHeight: "200px" }}>
                            {compail && (
                              <div
                                style={{
                                  padding: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                  fontSize: 18,
                                  color: "#555",
                                }}
                              >
                                <ThreeDots color="#009199" />
                                <span>Compiling and running your code…</span>
                              </div>
                            )}
                            {active === "run" && outputStatus && (
                              <div>
                                <h4 style={{ marginBottom: 10 }}>Compile Result</h4>

                                {outputStatus.useInput && runInput && (
                                  <div
                                    style={{
                                      marginBottom: 12,
                                      padding: 10,
                                      borderRadius: 6,
                                      background:
                                        outputStatus.executionStatus === 1
                                          ? "#E6FFE6"
                                          : "#FFE6E6",
                                      border:
                                        outputStatus.executionStatus === 1
                                          ? "1px solid #2E8B57"
                                          : "1px solid #B22222",
                                      fontFamily: "monospace",
                                      whiteSpace: "pre-wrap",
                                      maxHeight: '200px',
                                      maxWidth: "100%",
                                      overflow: "auto",
                                    }}
                                  >
                                    <strong>Input</strong>
                                    <pre style={{ margin: "6px 0 0 0" }}>{runInput}</pre>
                                  </div>
                                )}

                                <div
                                  style={{
                                    marginBottom: 12,
                                    padding: 10,
                                    borderRadius: 6,
                                    background:
                                      outputStatus.executionStatus === 1
                                        ? "#E6FFE6"
                                        : "#FFE6E6",
                                    border:
                                      outputStatus.executionStatus === 1
                                        ? "1px solid #2E8B57"
                                        : "1px solid #B22222",
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    maxHeight: '200px',
                                    maxWidth: "100%",
                                    overflow: "auto",
                                  }}
                                >
                                  <strong>Output</strong>
                                  {(outputStatus?.stderr && outputStatus.executionStatus === 2) && (
                                    <pre style={{ margin: "6px 0 4px 0", color: "#B22222" }}>
                                      {outputStatus.stderr.trim().split("\n").join("\n")}
                                    </pre>
                                  )}
                                  <pre style={{ margin: "6px 0 0 0", whiteSpace: "pre", minWidth: "max-content" }}>
                                    {outputStatus.stdout || outputStatus.actualOutput || "—"}
                                  </pre>
                                </div>

                                <div
                                  style={{
                                    fontWeight: "bold",
                                    color:
                                      outputStatus.executionStatus === 1 ? "green" : "red",
                                  }}
                                >
                                  Status:{" "}
                                  {outputStatus.executionStatus === 1
                                    ? "Success"
                                    : "Failed"}
                                </div>
                              </div>
                            )}

                            <div style={{ fontFamily: "monospace", marginTop: 10 }}>

                              {/* Summary line */}
                              {compileTestCases.length > 0 && (
                                <div
                                  style={{
                                    color: allPassed ? "#1B8E3E" : "#B00020",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    marginBottom: 12
                                  }}
                                >
                                  {allPassed
                                    ? `✓ All sample testcases passed! (${passCount}/${totalCount})`
                                    : `✗ Some sample testcases failed! (${passCount}/${totalCount})`}
                                </div>
                              )}

                              {/* Each testcase status */}
                              {compileTestCases.map((tc) => (
                                <div key={tc.testCaseId} style={{ marginBottom: 6 }}>
                                  <strong>Testcase {tc.testCaseId} :</strong>{" "}
                                  <span style={{ color: tc.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                    {tc.executionStatus === 1 ? "Passed" : "Failed"}
                                  </span>
                                </div>
                              ))}

                              {/* Details only for visible testcase */}
                              {firstVisible && (
                                <>
                                  <div style={{ marginTop: 14 }}>
                                    <div style={{ marginBottom: 6 }}>
                                      <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Status</strong>{" "}
                                      <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                        {firstVisible.executionStatus === 1 ? "Passed" : "Failed"}
                                      </span>
                                    </div>

                                    <div style={{ marginBottom: 6 }}>
                                      <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Execution time</strong>{" "}
                                      <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{outputStatus?.executionTime || "—"} ms</span>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                      <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Description</strong>{" "}
                                      <span style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>
                                        {firstVisible.executionStatus === 1
                                          ? "Testcase passed!"
                                          : "Output did not match expected result"}
                                      </span>
                                    </div>

                                    {firstVisible.input && (
                                      <div style={{ marginBottom: 10 }}>
                                        <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>Input</strong>
                                        <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.input}</pre>
                                      </div>
                                    )}

                                    <div style={{ marginBottom: 6 }}>
                                      <strong style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}> Output</strong>
                                      <pre style={{ color: firstVisible.executionStatus === 1 ? "#1B8E3E" : "#B00020" }}>{firstVisible.actualOutput || "—"}</pre>
                                    </div>

                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Fragment>
                ) : (
                  <div className="text-center" style={{ padding: 150 }}>
                    <img src="/images/green-check.svg" alt="" />
                    <h6>
                      {t(
                        "Your code was submitted successfully. You are now being redirected to the next question"
                      )}
                    </h6>
                    <button
                      className="output-btn"
                      onClick={() =>
                        !props.isNextTest && !props.isTestCompleted
                          ? goNext()
                          : props.isTestCompleted
                            ? props.setActive("feedback")
                            : props.isNextTest
                              ? props.getSummary()
                              : null
                      }
                    >
                      {t("View Next Question")}
                    </button>
                  </div>
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>
      {showSubmitTestPopup && (() => {
        const currentTestId = props.question?.candidateTestId;
        const currentTestHistory = currentTestId
          ? codingNav.history.filter(q => q.candidateTestId === currentTestId)
          : codingNav.history;
        const isQuestionSubmitted = (q) =>
          q.submitted === true || !!submittedNotebookIds[q.candidateQuestionId];
        const submittedCount = currentTestHistory.filter(isQuestionSubmitted).length;
        const totalQuestions = props.question?.totalQuestion || currentTestHistory.length;
        const skippedCount = currentTestHistory.filter(q =>
          q.candidateQuestionId !== props.question?.candidateQuestionId && !isQuestionSubmitted(q)
        ).length;
        return (
          <div className="upload-modal">
            <div className="modal-content" style={{ width: "500px" }}>
              <div className="modal-head">
                <div></div>
                <h3>Submit Test</h3>
                <img
                  src="/images/close-icon.svg"
                  alt=""
                  onClick={() => setShowSubmitTestPopup(false)}
                />
              </div>
              <div
                className="modal-input d-flex flex-column align-items-start"
                style={{ gap: "15px", padding: "15px" }}
              >
                <p style={{ margin: 0, fontSize: 15, color: "#333" }}>
                  <strong>Submitted Questions:</strong>{" "}{submittedCount} / {totalQuestions}
                </p>
                <p style={{ margin: 0, fontSize: 15, color: "#333" }}>
                  <strong>Skipped Questions:</strong>{" "}{skippedCount}
                </p>
                <p style={{ margin: 0, fontSize: 15, color: (isSubmitted || isNotebookSubmitted) ? "#1B8E3E" : "#B00020" }}>
                  <strong>Current Question:</strong>{" "}{(isSubmitted || isNotebookSubmitted) ? "Submitted" : "Not Submitted — submitting the test will skip this question"}
                </p>
              </div>
              <div className="d-flex justify-content-center" style={{ gap: "12px", paddingBottom: "15px" }}>
                <button
                  className="submit-btn"
                  onClick={() => setShowSubmitTestPopup(false)}
                  style={{ backgroundColor: "#aaa", cursor: "pointer" }}
                >
                  Close
                </button>
                <button
                  className="submit-btn"
                  onClick={async () => {
                    setShowSubmitTestPopup(false);
                    if (props.onBeforeTestCompleted) {
                      await props.onBeforeTestCompleted();
                    }
                    await dispatch(
                      updateTestStatus(
                        {
                          candidateTestDetailId: props.question?.candidateTestDetailId,
                          status: 3,
                        },
                        props.token
                      )
                    );
                    props.onTestCompleted?.();
                  }}
                  style={{ backgroundColor: "#f75891", cursor: "pointer" }}
                >
                  Submit Test
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {showNextConfirmPopup && (
        <div className="upload-modal">
          <div className="modal-content" style={{ width: "500px" }}>
            <div className="modal-head">
              <div></div>
              <h3>Unsaved Question</h3>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => setShowNextConfirmPopup(false)}
              />
            </div>
            <div
              className="modal-input d-flex flex-column align-items-start"
              style={{ gap: "15px", padding: "15px" }}
            >
              <p style={{ margin: 0, fontSize: 15, color: "#333" }}>
                You haven't submitted this question. Do you still want to proceed to the next question?
              </p>
            </div>
            <div className="d-flex justify-content-center" style={{ gap: "12px", paddingBottom: "15px" }}>
              <button
                className="submit-btn"
                onClick={() => setShowNextConfirmPopup(false)}
                style={{ backgroundColor: "#aaa", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                className="submit-btn"
                onClick={() => {
                  setShowNextConfirmPopup(false);
                  const payload = pendingNextPayloadRef.current;
                  pendingNextPayloadRef.current = null;
                  if (props.question?.questionTypeID === 13) {
                    setNotebookLoading(true);
                  }
                  setNavLoading(true);
                  props.onNext(payload);
                }}
                style={{ backgroundColor: "#f75891", cursor: "pointer" }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Main;